/* eslint-disable eqeqeq */
//@ts-nocheck
import { CFormCheck, CFormInput, CSpinner } from "@coreui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import FileUploaderV2 from "../../../components/ui/FileUploaderV2";
import { Field, FormSection } from "../../../components/ui/FormUi";
import InfiniteScrollSelect from "../../../components/ui/InfiniteScrollSelect";
import { FormMode } from "../../../helpers/enums";
import { useAuth } from "../../../hooks/useAuth";
import { useCookie } from "../../../hooks/useCookie";
import apiService from "../../../service/apiService";
import { cardStyle } from "../../Places/PlacesTable";
import RestrictedRoute from "../../auth/RestrictedRoute";
import { SwitchCard } from "../../../components/ui/ToggleUi"; // adjust path

const schema = yup.object().shape({
  relatedType: yup.string().required("Ce champ est obligatoire"),
  relationId: yup
    .string()
    .nullable()
    .when("relatedType", (relatedType, schema) => {
      if (!relatedType.includes("link")) {
        return schema.required("Ce champ est obligatoire");
      }
      return schema.nullable();
    }),
  imageLargeFrench: yup
    .mixed()
    .nullable()
    .when("relatedType", (relatedType, schema) => {
      if (relatedType.includes("link")) {
        return schema.required("L'image de la publicité est obligatoire");
      }
      return schema.nullable();
    }),
  imageSmallFrench: yup.mixed().nullable(),
  isActive: yup.boolean().required("Ce champ est obligatoire"),
  url: yup.string().when("relatedType", (relatedType, schema) => {
    if (relatedType.includes("link")) {
      return schema.required("Le lien est obligatoire");
    }
    return schema.nullable();
  }),
  order: yup
    .number()
    // @ts-ignore
    .nullable(true)
    .typeError("")
    .transform((_, val) => {
      if (val == "") return null;
      const result = val == Number(val) ? Number(val) : null;
      return result;
    }),
});

const LANGS = [
  { key: "French", title: "Français", required: true },
  { key: "English", title: "Anglais", required: false },
  { key: "Arabic", title: "Arabe", required: false },
];

const TARGETS = [
  { value: "place", label: "Établissements" },
  { value: "offer", label: "Offres" },
  { value: "event", label: "Événements" },
  { value: "link", label: "Lien" },
];

const emptyImages = {
  imageLargeFrench: null,
  imageSmallFrench: null,
  imageLargeEnglish: null,
  imageSmallEnglish: null,
  imageLargeArabic: null,
  imageSmallArabic: null,
};

export default function NewAdvertisement({ mode = FormMode.CREATE }) {
  const [formLoading, setFormLoading] = useState(false);
  const { token } = useCookie("vToken");
  const navigate = useNavigate();
  const params = useParams();
  const { isAdmin } = useAuth();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  // Only newly picked files live here; existing images come from `publication`
  const [images, setImages] =
    useState<Record<string, File | null>>(emptyImages);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control: formControl,
    watch: watchFormValue,
    setValue: setFormValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      relatedType: "place",
      isActive: true,
    },
  });

  const relatedType = watchFormValue("relatedType");
  const isLink = relatedType === "link";
  const isCreate = mode !== FormMode.UPDATE;

  const { data: publication } = useQuery(
    ["publication", params.id],
    () => apiService.MakeGetRequest(`publications/${params.id}`, token),
    { enabled: mode === FormMode.UPDATE },
  );

  useEffect(() => {
    if (mode === FormMode.UPDATE && publication) {
      setFormValue("relatedType", publication.relatedType);
      const id =
        publication.offer?.id || publication.event?.id || publication.place?.id;
      setFormValue("relationId", id);
      Object.keys(emptyImages).forEach((key) =>
        setFormValue(key, publication[key]),
      );
      setFormValue("isActive", publication.isActive);
      setFormValue("order", publication.order);
      if (publication.relatedType === "link") {
        setFormValue("url", publication.url || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publication]);

  const createAdvertisementMutation = useMutation(async (data: FormData) =>
    apiService.MakePostRequest("publications", data, token, true),
  );

  const updateAdvertisementMutation = useMutation(async (data: FormData) =>
    apiService.MakePutRequest(`publications/${params.id}`, data, token, true),
  );

  const prepareFormData = (data: any) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (!(key in emptyImages)) {
        formData.append(key, data[key]);
      }
    });
    Object.entries(images).forEach(([key, file]) => {
      if (file instanceof File) formData.append(key, file);
    });
    return formData;
  };

  const onSubmit = async (values: any) => {
    const formData = prepareFormData(values);
    try {
      setFormLoading(true);
      if (mode === FormMode.UPDATE) {
        await updateAdvertisementMutation.mutateAsync(formData);
        toast.success("Publicité mise à jour");
      } else {
        await createAdvertisementMutation.mutateAsync(formData);
        toast.success("Publicité créée");
      }
      navigate("/advertisement");
    } catch (error) {
      setFormLoading(false);
      toast.error(
        error?.response?.data?.message ||
          (mode === FormMode.UPDATE
            ? "Échec de la mise à jour de la publicité"
            : "Échec de la création de la publicité"),
      );
    }
  };

  const pickImage = (name: string, file: File | null) => {
    setImages((prev) => ({ ...prev, [name]: file }));
    setFormValue(name, file);
  };

  const offerEndpoint = !isAdmin ? "specials/ours" : "specials/all";
  const relationPath =
    relatedType === "event"
      ? "events/ours"
      : relatedType === "offer"
        ? offerEndpoint
        : "places";

  const defaultLabel =
    relatedType === "event"
      ? publication?.event?.name
      : relatedType === "offer"
        ? publication?.offer?.title
        : publication?.place?.name;

  return (
    <RestrictedRoute>
      <div className="vz-form px-2 mt-4">
        {/* Header */}
        <div
          style={{ ...cardStyle, padding: "16px 20px" }}
          className="d-flex justify-content-between align-items-center mb-3"
        >
          <div>
            <h4 className="fs-4 fw-bold mb-0">
              {isCreate ? "Ajouter une publicité" : "Modifier la publicité"}
            </h4>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Les champs marqués d'une * sont obligatoires
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/advertisement")}
            className="btn btn-outline-secondary rounded-pill px-3"
          >
            ← Retour
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Target */}
          <FormSection title="Cible de la publicité">
            <Field
              col="col-12"
              label="Cible"
              required
              error={errors.relatedType?.message}
            >
              <Controller
                name="relatedType"
                control={formControl}
                render={({ field }) => (
                  <div className="d-flex flex-wrap gap-4 pt-1">
                    {TARGETS.map((t) => (
                      <CFormCheck
                        key={t.value}
                        type="radio"
                        id={`target_${t.value}`}
                        label={t.label}
                        value={t.value}
                        checked={field.value === t.value}
                        onChange={() => {
                          field.onChange(t.value);
                          if (t.value !== "link") setSelectedItem(null);
                        }}
                      />
                    ))}
                  </div>
                )}
              />
            </Field>

            {isLink ? (
              <Field
                label="URL de la publicité"
                htmlFor="linkUrl"
                required
                error={errors.url?.message}
              >
                <CFormInput
                  id="linkUrl"
                  placeholder="https://..."
                  {...register("url")}
                />
              </Field>
            ) : (
              <Field
                label={
                  relatedType === "event"
                    ? "Événement"
                    : relatedType === "offer"
                      ? "Offre"
                      : "Établissement"
                }
                htmlFor="relationId"
                required
                error={errors.relationId?.message}
              >
                <Controller
                  name="relationId"
                  control={formControl}
                  render={({ field }) => (
                    <InfiniteScrollSelect
                      key={relatedType}
                      id="relationId"
                      getOptionLabel={(o) => o?.name || o?.title}
                      getOptionValue={(o) => o.id}
                      isOptionSelected={(o, selected) =>
                        o?.id === selected?.[0]?.id
                      }
                      url={{ path: relationPath }}
                      value={
                        selectedItem ||
                        (defaultLabel && {
                          id: field.value,
                          name: defaultLabel,
                        }) ||
                        null
                      }
                      onChange={(item: any) => {
                        setSelectedItem(item);
                        field.onChange(item?.id || "");
                      }}
                    />
                  )}
                />
              </Field>
            )}
          </FormSection>

          {/* Images per language */}
          {LANGS.map(({ key, title, required }) => (
            <FormSection
              key={key}
              title={`Images · ${title}`}
              description={required ? undefined : "Optionnel"}
            >
              <Field
                label="Image grande taille (1600×540)"
                required={required}
                error={errors[`imageLarge${key}`]?.message}
              >
                <FileUploaderV2
                  id={`imageLarge${key}`}
                  accept="image/png,image/jpeg,image/jpg"
                  existingUrl={publication?.[`imageLarge${key}`]}
                  file={images[`imageLarge${key}`]}
                  onChange={(f) => pickImage(`imageLarge${key}`, f)}
                />
              </Field>
              <Field
                label="Image petite taille (1080×1080)"
                required={required}
                error={errors[`imageSmall${key}`]?.message}
              >
                <FileUploaderV2
                  id={`imageSmall${key}`}
                  accept="image/png,image/jpeg,image/jpg"
                  existingUrl={publication?.[`imageSmall${key}`]}
                  file={images[`imageSmall${key}`]}
                  onChange={(f) => pickImage(`imageSmall${key}`, f)}
                />
              </Field>
            </FormSection>
          ))}

          {/* Options */}
          <FormSection title="Options">
            <div className="col-md-6">
              <SwitchCard
                id="isActive"
                label="Activer la publicité"
                description="La publicité est visible dans l'application"
                {...register("isActive")}
              />
            </div>
            <Field label="Ordre" htmlFor="order" error={errors.order?.message}>
              <CFormInput id="order" type="number" {...register("order")} />
            </Field>
          </FormSection>

          {/* Sticky actions */}
          <div
            className="d-flex justify-content-end gap-2 py-3 px-3 mt-3"
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              backgroundColor: "#fff",
              borderRadius: 16,
              boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
            }}
          >
            <button
              type="button"
              disabled={formLoading}
              onClick={() => navigate("/advertisement")}
              className="btn btn-outline-secondary rounded-pill px-4"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="btn btn-success text-white rounded-pill px-4"
            >
              {formLoading ? <CSpinner size="sm" /> : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </RestrictedRoute>
  );
}
